import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  ArrowLeft, Building2, Plus, Loader2, CheckCircle2,
  AlertCircle, Search, ChevronRight, Crown
} from 'lucide-react'
import { useAuth } from '../hooks/use-auth'
import { useI18n } from '../i18n'
import { get, post } from '../helper/network'
import API from '../helper/api'

interface EnterpriseItem {
  id: string
  name: string
  logoUrl?: string
  industryType?: string
  shortCode?: string
  status?: string
}

export default function EnterpriseBindPage() {
  const navigate = useNavigate()
  const { user, isLoggedIn, refreshUserInfo } = useAuth()
  const { t } = useI18n()

  const [enterprises, setEnterprises] = useState<EnterpriseItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [bindingId, setBindingId] = useState<string | null>(null)
  const [bindMsg, setBindMsg] = useState<{ id: string; ok: boolean; text: string } | null>(null)

  // 新建企业
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createMsg, setCreateMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [createForm, setCreateForm] = useState({
    name: '',
    industryType: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    shortCode: '',
  })

  const isAdmin = user?.role === 'TENANT_ADMIN'

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login'); return }
    loadEnterprises()
  }, [isLoggedIn])

  const loadEnterprises = async () => {
    setLoading(true)
    try {
      const res: any = await get(API.GET_ENTERPRISE_LIST, {})
      if (res && res.code === 200 && res.data) {
        setEnterprises(Array.isArray(res.data) ? res.data : [])
      }
    } catch {}
    setLoading(false)
  }

  const handleBind = async (enterpriseId: string) => {
    if (!user?.username) return
    setBindingId(enterpriseId)
    setBindMsg(null)
    try {
      // Need userId from getUserInfo
      const infoRes: any = await get(API.GET_USER_INFO, { username: user.username })
      const userId = infoRes?.data?.id
      if (!userId) {
        setBindMsg({ id: enterpriseId, ok: false, text: t('用户信息加载失败', 'User info not loaded') })
        setBindingId(null)
        return
      }

      const res: any = await post(API.POST_BIND_ENTERPRISE, { enterpriseId, userId })
      if (res && res.code === 200) {
        setBindMsg({ id: enterpriseId, ok: true, text: t('绑定成功', 'Bound successfully') })
        refreshUserInfo()
        setTimeout(() => navigate('/enterprise'), 1200)
      } else {
        setBindMsg({ id: enterpriseId, ok: false, text: res?.message || t('绑定失败', 'Bind failed') })
      }
    } catch {
      setBindMsg({ id: enterpriseId, ok: false, text: t('网络错误', 'Network error') })
    }
    setBindingId(null)
  }

  const handleCreate = async () => {
    if (!createForm.name.trim()) return
    setCreating(true)
    setCreateMsg(null)
    try {
      const res: any = await post(API.POST_CREATE_ENTERPRISE, createForm)
      if (res && res.code === 200) {
        setCreateMsg({ ok: true, text: t('创建成功', 'Created successfully') })
        refreshUserInfo()
        loadEnterprises()
        setTimeout(() => {
          setShowCreate(false)
          setCreateForm({ name: '', industryType: '', contactName: '', contactEmail: '', contactPhone: '', shortCode: '' })
        }, 1200)
      } else {
        setCreateMsg({ ok: false, text: res?.message || t('创建失败', 'Create failed') })
      }
    } catch {
      setCreateMsg({ ok: false, text: t('网络错误', 'Network error') })
    }
    setCreating(false)
  }

  const filtered = enterprises.filter((e) =>
    !searchQuery.trim() ||
    e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (e.industryType && e.industryType.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  if (!isLoggedIn) return null

  return (
    <div className="min-h-screen bg-[#fafafa] relative overflow-hidden">
      {/* 装饰背景 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full opacity-40"
          style={{ background: 'radial-gradient(circle, rgba(254,215,170,0.3), transparent 70%)' }} />
        <div className="absolute -bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(199,210,254,0.3), transparent 70%)' }} />
      </div>

      <div className="max-w-2xl mx-auto px-4 py-12 relative z-10">
        <Link to="/enterprise" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" />
          {t('返回企业管理', 'Back to Enterprise')}
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-50 border border-orange-100 mb-4">
            <Building2 className="h-7 w-7 text-orange-500" />
          </div>
          <h1 className="text-xl font-semibold text-[#1a1a1a]">{t('加入企业', 'Join an Enterprise')}</h1>
          <p className="text-sm text-gray-400 mt-1">{t('选择一个企业加入，开始使用AI管家', 'Select an enterprise to join and start using AI butler')}</p>
        </div>

        {/* 已绑定提示 */}
        {user?.enterpriseName && (
          <div className="mb-6 p-4 rounded-2xl bg-orange-50/60 border border-orange-100/50 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-orange-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-700">{t('已绑定企业', 'Current Enterprise')}</p>
              <p className="text-xs text-orange-500 truncate">{user.enterpriseName}</p>
            </div>
            <Link to="/enterprise">
              <Button variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50 h-8 text-xs rounded-xl">
                {t('查看', 'View')}
              </Button>
            </Link>
          </div>
        )}

        {/* 管理员创建企业入口 */}
        {isAdmin && (
          <div className="mb-6">
            <button
              onClick={() => { setShowCreate(!showCreate); setCreateMsg(null) }}
              className="w-full p-4 rounded-2xl bg-white border border-dashed border-orange-200 hover:border-orange-400 hover:bg-orange-50/30 transition-all flex items-center gap-3 group"
            >
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                <Plus className="h-5 w-5 text-orange-500" />
              </div>
              <div className="text-left flex-1">
                <p className="text-sm font-medium text-[#1a1a1a]">{t('创建新企业', 'Create Enterprise')}</p>
                <p className="text-xs text-gray-400">{t('管理员可创建企业并配置基本信息', 'Admins can create and configure enterprises')}</p>
              </div>
              <ChevronRight className={`h-4 w-4 text-gray-300 transition-transform ${showCreate ? 'rotate-90' : ''}`} />
            </button>

            {showCreate && (
              <div className="mt-3 p-5 rounded-2xl bg-white border border-black/[0.04] space-y-4">
                <h3 className="text-sm font-medium text-[#1a1a1a]">{t('新建企业', 'New Enterprise')}</h3>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">{t('企业名称', 'Enterprise Name')} *</Label>
                    <Input
                      value={createForm.name}
                      onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder={t('请输入企业名称', 'Enter enterprise name')}
                      className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('行业类型', 'Industry')}</Label>
                      <Input
                        value={createForm.industryType}
                        onChange={(e) => setCreateForm((p) => ({ ...p, industryType: e.target.value }))}
                        placeholder={t('如：互联网', 'e.g., Internet')}
                        className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('企业短码', 'Short Code')}</Label>
                      <Input
                        value={createForm.shortCode}
                        onChange={(e) => setCreateForm((p) => ({ ...p, shortCode: e.target.value }))}
                        placeholder="mycompany"
                        className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06] font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">{t('联系人', 'Contact Name')}</Label>
                    <Input
                      value={createForm.contactName}
                      onChange={(e) => setCreateForm((p) => ({ ...p, contactName: e.target.value }))}
                      className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('联系邮箱', 'Email')}</Label>
                      <Input
                        type="email"
                        value={createForm.contactEmail}
                        onChange={(e) => setCreateForm((p) => ({ ...p, contactEmail: e.target.value }))}
                        className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{t('联系电话', 'Phone')}</Label>
                      <Input
                        value={createForm.contactPhone}
                        onChange={(e) => setCreateForm((p) => ({ ...p, contactPhone: e.target.value }))}
                        className="h-9 text-sm rounded-xl bg-white/60 border-black/[0.06]"
                      />
                    </div>
                  </div>
                </div>

                {createMsg && (
                  <div className={`flex items-center gap-1.5 text-xs ${createMsg.ok ? 'text-emerald-500' : 'text-red-500'}`}>
                    {createMsg.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                    {createMsg.text}
                  </div>
                )}

                <Button
                  onClick={handleCreate}
                  className="bg-orange-500 text-white hover:bg-orange-600 h-9 text-sm rounded-xl px-6"
                  disabled={creating || !createForm.name.trim()}
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
                  {t('创建企业', 'Create Enterprise')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* 企业列表 */}
        <div className="rounded-2xl bg-white/70 backdrop-blur-xl border border-white/80 shadow-[0_4px_24px_rgba(0,0,0,0.04)] overflow-hidden">
          <div className="p-4 border-b border-black/[0.04]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('搜索企业名称或行业', 'Search by name or industry')}
                className="h-9 text-sm rounded-xl bg-gray-50/80 border-black/[0.04] pl-9"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">{t('暂无可加入的企业', 'No enterprises available')}</p>
              {isAdmin && (
                <p className="text-xs text-gray-300 mt-1">{t('点击上方创建新企业', 'Click above to create one')}</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-black/[0.04]">
              {filtered.map((ent) => (
                <div key={ent.id} className="p-4 hover:bg-orange-50/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100/50 flex items-center justify-center shrink-0 overflow-hidden">
                      {ent.logoUrl ? (
                        <img src={ent.logoUrl} alt="" className="w-11 h-11 rounded-xl object-cover" />
                      ) : (
                        <Building2 className="h-5 w-5 text-orange-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-[#1a1a1a] truncate">{ent.name}</p>
                        {ent.shortCode && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">{ent.shortCode}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{ent.industryType || '-'}</p>
                    </div>

                    {user?.enterpriseId === ent.id ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {t('已加入', 'Joined')}
                      </span>
                    ) : (
                      <Button
                        onClick={() => handleBind(ent.id)}
                        className="bg-orange-500 text-white hover:bg-orange-600 h-8 text-xs rounded-xl px-4 shrink-0"
                        disabled={bindingId === ent.id}
                      >
                        {bindingId === ent.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('加入', 'Join')}
                      </Button>
                    )}
                  </div>

                  {bindMsg && bindMsg.id === ent.id && (
                    <div className={`flex items-center gap-1 text-xs mt-2 ml-14 ${bindMsg.ok ? 'text-emerald-500' : 'text-red-500'}`}>
                      {bindMsg.ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                      {bindMsg.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
